/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { createRoute, redirect } from '@tanstack/react-router';
import { lazy } from 'react';
import { z } from 'zod';

import { AGREEMENT_SORTS } from '@/api/agreements';
import { meQuery } from '@/api/auth';
import { COUNTERPARTY_SORTS } from '@/api/counterparties';
import { PAGE_SIZES } from '@/lib/paging';
import { isSessionEnded } from '@/lib/queryClient';
import { asksOnlyForRecords } from '@/lib/stages';
import { type RouterContext, rootRoute } from '@/routes/root';

/**
 * The route table.
 *
 * Every route is code-split (`FE-5`): opening the dashboard must not download
 * the admin screens. Filters live in the URL rather than in component state, so
 * a filtered list is a link somebody can send (`SCR-4`).
 */

const lazyPage = <T extends Record<string, unknown>>(
  load: () => Promise<T>,
  name: keyof T,
) => lazy(async () => ({ default: (await load())[name] as never }));

/**
 * Redirect to sign-in unless there is a live session.
 *
 * Only a 401 means that. Anything else -- the API down, a gateway timing out
 * -- is not a reason to send somebody to sign in again, which would not help
 * and would hide what went wrong. That is thrown on, to the route's error
 * state, which offers to try again.
 */
/* eslint-disable @typescript-eslint/only-throw-error --
   Throwing a `redirect()` is how TanStack Router cancels navigation and sends
   the browser elsewhere. It is the framework's own idiom, not a thrown
   non-error. */
async function requireSession({
  context,
  location,
}: {
  context: RouterContext;
  location: { href: string };
}): Promise<void> {
  try {
    await context.queryClient.query(meQuery());
  } catch (error) {
    if (!isSessionEnded(error)) throw error;
    // Where they were going, carried through sign-in. A link in an email is
    // opened by somebody who is not signed in yet, and landing them on the
    // dashboard means they have to go and find the thing the email was about.
    throw redirect({ to: '/signin', search: { next: location.href } });
  }
}

export const signInRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signin',
  // `next` carries where they were going. Why somebody is here — signed out
  // for being idle, say — travels in sessionStorage, because a reason left in
  // the URL is repeated on every later visit to the page.
  validateSearch: z.object({
    next: z.string().optional(),
    error: z.string().optional(),
  }),
  component: lazyPage(() => import('@/pages/SignIn'), 'SignIn'),
});

export const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  beforeLoad: requireSession,
  // `waiting=all`: Waiting on you lists every thing it counts, not the newest four.
  validateSearch: z.object({
    waiting: z.literal('all').optional().catch(undefined),
  }),
  component: lazyPage(() => import('@/pages/Dashboard'), 'Dashboard'),
});

/** An ISO day, `2026-09-29`, from a date input; anything else is dropped. */
const isoDay = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .optional()
  .catch(undefined);

/**
 * What the agreements and records lists share: the filters, the sort and the
 * page. Coerced and dropped when malformed, so a mistyped link still opens
 * the list, only unfiltered. Every one is the server's to apply.
 */
const listSearch = {
  /** The agreement type: a workflow, by its id. */
  type: z.coerce.string().optional().catch(undefined),
  /** A counterparty, by its id. */
  cp: z.coerce.string().optional().catch(undefined),
  /** Who started it, by their id. */
  owner: z.coerce.string().optional().catch(undefined),
  /** The team that owns its workflow, by the team's key. */
  team: z.coerce.string().optional().catch(undefined),
  from: isoDay,
  to: isoDay,
  sort: z.enum(AGREEMENT_SORTS).optional().catch(undefined),
  dir: z.enum(['asc', 'desc']).optional().catch(undefined),
  page: z.coerce.number().int().min(0).optional().catch(undefined),
  size: z.coerce
    .number()
    .int()
    .refine((n) => (PAGE_SIZES as readonly number[]).includes(n))
    .optional()
    .catch(undefined),
};

/**
 * What Agreements filters by, on either tab.
 *
 * Coerced, because the router reads a query string as JSON where it can:
 * `?year=2026` arrives as a number and `?q=2026` would too. A value that is
 * not what it should be is dropped rather than failing the whole screen --
 * a mistyped link still opens the list, only unfiltered.
 */
const agreementSearch = z.object({
  /** Which tab: the work in progress, or what is completed. `packets` was a third. */
  tab: z.enum(['completed', 'packets']).optional().catch(undefined),
  /**
   * In progress: agreements started on their own, or packets (and the steps
   * of packets not listed as rows). Completed: agreements on their own, or a
   * packet's steps. Left out, both.
   */
  kind: z.enum(['regular', 'packet']).optional().catch(undefined),
  /** In progress only: a packet's bundle, by its id. */
  ptype: z.coerce.string().optional().catch(undefined),
  stage: z.string().optional(),
  q: z.coerce.string().optional().catch(undefined),
  /** Completed only: a workflow by its name, the older link, still honoured. */
  workflow: z.coerce.string().optional().catch(undefined),
  /** Completed only: the year it was signed in. */
  year: z.coerce.number().int().positive().optional().catch(undefined),
  /**
   * Completed only: the days it was started, where `from` and `to` are the
   * days it was signed. A dashboard figure counted by the day each agreement
   * was started -- the signed ones among what a person started, say -- opens
   * the Completed tab narrowed by these.
   */
  startedFrom: isoDay,
  startedTo: isoDay,
  ...listSearch,
});

export const agreementsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agreements',
  beforeLoad: async ({ context, location, search }) => {
    await requireSession({ context, location });
    // Packets started had a tab of their own; they are the In progress and
    // Completed lists narrowed to packets now.
    if (search.tab === 'packets') {
      throw redirect({ to: '/agreements', search: { kind: 'packet' } });
    }
    // A link that asks only for finished agreements is asking for the
    // Completed tab: `?stage=filed` would otherwise open a list of the work
    // in progress that cannot hold what it names. The stage and the other
    // filters carry over, so the list holds what the link asked for; the days
    // it was started move to the Completed tab's own `startedFrom` and
    // `startedTo`, since its `from` and `to` are the days it was signed.
    if (search.tab !== 'completed' && asksOnlyForRecords(search.stage)) {
      const { from, to, ...kept } = search;
      throw redirect({
        to: '/agreements',
        search: {
          ...kept,
          page: undefined,
          tab: 'completed',
          ...(from ? { startedFrom: from } : {}),
          ...(to ? { startedTo: to } : {}),
        },
      });
    }
  },
  validateSearch: agreementSearch,
  component: lazyPage(() => import('@/pages/Agreements'), 'Agreements'),
});

/**
 * Records was its own page; it is the Completed tab of Agreements now. The
 * address still works, and keeps whatever it was filtered by.
 */
export const recordsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/records',
  validateSearch: agreementSearch,
  beforeLoad: ({ search }) => {
    throw redirect({
      to: '/agreements',
      search: { ...search, stage: undefined, tab: 'completed' },
    });
  },
});

export const agreementRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agreements/$agreementId',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/AgreementDetail'), 'AgreementDetail'),
});

/**
 * Where an agreement on the counterparty's document is signed. Split like
 * every route, and more than most: it is the only screen that draws a PDF,
 * and nothing else should download the code that does.
 */
export const agreementSpotsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/agreements/$agreementId/spots',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/CounterpartySpots'), 'CounterpartySpots'),
});

/** Templates: agreement templates, or `?tab=packets` for packet templates. */
export const workflowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workflows',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/Templates'), 'Templates'),
});

export const workflowRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workflows/$workflowId',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/WorkflowDetail'), 'WorkflowDetail'),
});

/**
 * A launch from a packet carries which packet and which step, so the form is
 * prefilled from it and the agreement becomes that step (`PKT-16`). Coerced
 * and dropped when malformed, as Records' filters are: a mistyped link still
 * opens the ordinary form.
 */
const launchSearch = z.object({
  packet: z.coerce.string().pipe(z.uuid()).optional().catch(undefined),
  step: z.coerce.number().int().positive().optional().catch(undefined),
});

export const launchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/workflows/$workflowId/launch',
  beforeLoad: requireSession,
  validateSearch: launchSearch,
  component: lazyPage(() => import('@/pages/Launch'), 'Launch'),
});

/** Packet templates are a tab of Templates now; the old address opens it. */
export const packetsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/packets',
  beforeLoad: () => {
    throw redirect({ to: '/workflows', search: { tab: 'packets' } });
  },
});

export const packetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/packets/$packetId',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/PacketDetail'), 'PacketDetail'),
});

export const deadlinesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/deadlines',
  // `expiring`: only the signed agreements expiring within the one window,
  // as the dashboard's Expiring soon counts them.
  validateSearch: z.object({
    show: z.enum(['expiring']).optional().catch(undefined),
  }),
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/Deadlines'), 'Deadlines'),
});

/**
 * What the Counterparties list is narrowed and paged by. The list only grows,
 * so the server searches, sorts and pages it (`DB-6`); a malformed value is
 * dropped rather than failing the screen.
 */
const counterpartySearch = z.object({
  q: z.coerce.string().optional().catch(undefined),
  sort: z.enum(COUNTERPARTY_SORTS).optional().catch(undefined),
  dir: z.enum(['asc', 'desc']).optional().catch(undefined),
  page: z.coerce.number().int().min(0).optional().catch(undefined),
  size: listSearch.size,
});

export const counterpartiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/counterparties',
  beforeLoad: requireSession,
  validateSearch: counterpartySearch,
  component: lazyPage(() => import('@/pages/Counterparties'), 'Counterparties'),
});

export const importRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/import',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/Import'), 'Import'),
});

export const profileRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/profile',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/settings/Profile'), 'Profile'),
});

export const peopleRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/people',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/settings/People'), 'People'),
});

export const groupsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/groups',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/settings/Groups'), 'Groups'),
});

/** Merging counterparties that share a name (`CQ-09`): an Admin's; the server checks. */
export const mergeCounterpartiesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/counterparties',
  beforeLoad: requireSession,
  component: lazyPage(
    () => import('@/pages/settings/MergeCounterparties'),
    'MergeCounterparties',
  ),
});

export const companyRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/company',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/settings/Company'), 'Company'),
});

/** The whole audit trail, for a holder of `audit:read`; the server checks. */
export const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/audit',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/settings/AuditTrail'), 'AuditTrail'),
});

export const integrationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/integrations',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/settings/Integrations'), 'Integrations'),
});

export const notificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/notifications',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/settings/Notifications'), 'Notifications'),
});

// Everybody may read Amida's holidays; only an Admin changes them (`LC-14`).
export const holidaysRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/holidays',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/settings/Holidays'), 'Holidays'),
});

// Every person may be away, so every person signed in may set it (`OOO-4`).
export const outOfOfficeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/out-of-office',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/settings/OutOfOffice'), 'OutOfOffice'),
});

// Open to everybody signed in: knowing how something works is never a
// permission. Split like every route, so the guide's text is fetched only by
// somebody who opens it.
export const helpRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/help',
  beforeLoad: requireSession,
  component: lazyPage(() => import('@/pages/Help'), 'Help'),
});

/** Bare /settings goes to the organization's settings every person can open. */
export const settingsIndexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  beforeLoad: () => {
    throw redirect({ to: '/settings/integrations' });
  },
});

export const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/dashboard' });
  },
});

export const routeTree = rootRoute.addChildren([
  homeRoute,
  signInRoute,
  dashboardRoute,
  agreementsRoute,
  agreementRoute,
  agreementSpotsRoute,
  recordsRoute,
  workflowsRoute,
  workflowRoute,
  launchRoute,
  packetsRoute,
  packetRoute,
  deadlinesRoute,
  counterpartiesRoute,
  importRoute,
  settingsIndexRoute,
  profileRoute,
  peopleRoute,
  groupsRoute,
  mergeCounterpartiesRoute,
  companyRoute,
  integrationsRoute,
  auditRoute,
  notificationsRoute,
  outOfOfficeRoute,
  holidaysRoute,
  helpRoute,
]);
/* eslint-enable @typescript-eslint/only-throw-error */
